import { supabase } from './supabase'

/**
 * Invitations (migration 045).
 *
 * ── AN INVITATION IS A ROW, NOT AN EMAIL ────────────────────────────────────
 *
 * Migration 010 makes the identity row the invitation: its trigger refuses any
 * auth.users insert for an address that has no identity, so creating the row is
 * exactly what makes somebody able to sign in. There is no invitation object
 * and there must not be one — a second table would be a second answer to "may
 * this address in", and the two would disagree.
 *
 * That is why `invitePerson` returns without having sent anything, and why
 * sending is a separate call below.
 *
 * ── WHAT ARRIVES, AND WHY THAT KEEPS CHANGING ───────────────────────────────
 *
 * Whichever sender is configured, what lands is a bare six-digit code with no
 * mention of ConeTec and no explanation of what the hub is. Making it a welcome
 * is FND-14. **A working button here is not a working onboarding path**, and
 * the roster must not be bulk-invited on the strength of one that sends.
 *
 * The transport underneath has moved twice in a day, so this comment states the
 * durable shape rather than today's setting:
 *
 *   - Custom SMTP → Resend on oneconetec.com is the intended end state. It was
 *     configured and returned 550 on every message because the DOMAIN is
 *     unverified, not because SMTP was wrong. Two SPF records on the `send`
 *     subdomain are all that is missing; DKIM already verifies. DNS is at
 *     GANDI and belongs to SFIT (ACC-20).
 *   - Supabase's DEFAULT sender is the interim, David's call on 10 Sep, so
 *     invitations can go out while that is unblocked. It is rate limited and
 *     sends from a Supabase address, which for ~900 people on Microsoft mail
 *     will read as phishing — fine for the five of us, never for launch.
 *
 * So DO NOT hard-code "this will fail" or name a cause in anything a user
 * reads. That claim was true for about an hour and would have been wrong for
 * the next fortnight — which is precisely how the "Supabase default sender"
 * line in CLAUDE.md came to be wrong for days.
 *
 * WHAT NEVER CHANGES, and it is why the two calls are separate: the person, the
 * identity row and the membership are committed before the send is attempted,
 * and GoTrue rolls its own auth.users row back when mail fails. A failed send
 * leaves NO half-state. Proven 10 Sep: Mary Nguyen and Lisa Blackham were
 * invited while every send was 500ing, and both read as "Invited, not used"
 * with their codes re-sendable.
 */

/** Create a colleague and give them a way in. Returns the new person id. */
export async function invitePerson(input: {
  displayName: string
  email: string
  locale?: string
  givenName?: string | null
  familyName?: string | null
  orgUnitId?: string | null
}): Promise<string> {
  const { data, error } = await supabase.rpc('invite_person', {
    p_display_name: input.displayName.trim(),
    p_email: input.email.trim(),
    p_locale: input.locale ?? 'en-CA',
    p_given_name: input.givenName || null,
    p_family_name: input.familyName || null,
    p_org_unit_id: input.orgUnitId || null,
  })
  if (error) throw error
  return data as string
}

/**
 * Give an existing person a way in. Separate from invitePerson deliberately —
 * see 045. This is ACC-14: Lisa Blackham has a membership and no identity, so
 * she owns 26 Wiki One answers she cannot see.
 */
export async function inviteExistingPerson(personId: string, email: string): Promise<void> {
  const { error } = await supabase.rpc('invite_existing_person', {
    p_person_id: personId,
    p_email: email.trim(),
  })
  if (error) throw error
}

/**
 * Send somebody a sign-in code, and record that we did.
 *
 * TWO CALLS, AND THE ORDER MATTERS. Only Supabase Auth can send; only the
 * database can remember. The send goes first, because recording a send that
 * never happened is worse than a send we failed to record — the first makes the
 * screen say "invited yesterday" about somebody who was never contacted, and
 * chasing people is the whole job between now and 13 October.
 *
 * `shouldCreateUser` is left at its default. Migration 010's trigger is what
 * enforces invite-only: an address with no identity row is refused at the
 * auth.users insert, and that refusal is the guard, not a flag passed from a
 * browser. If this call fails with a message about no invitation existing, that
 * is the system working.
 *
 * This does NOT sign the administrator out or touch their session — a code is
 * sent, and nothing changes until somebody verifies it in their own browser.
 */
export async function sendSignInCode(personId: string, email: string): Promise<void> {
  const { error } = await supabase.auth.signInWithOtp({ email: email.trim() })
  if (error) {
    // A failure here is the mail transport, not the invitation, and the two
    // look identical to an administrator. The wrong reading is "the invite is
    // broken, add them again", which then hits the duplicate-address guard and
    // looks broken twice.
    //
    // Deliberately NAMES NO CAUSE. The transport changed twice on 10 Sep, and a
    // message that diagnoses one of them is wrong for the other — the mistake
    // this codebase keeps making with stale claims. The provider's own text is
    // passed through instead; auth_logs carries the real reason.
    if (error.status === 500 || error.status === 429) {
      throw new Error(
        `The invitation is saved — the code could not be sent. That is the ` +
          `mail provider, not the hub. Re-send it from their record; do not ` +
          `invite them again. [${error.message}]`,
      )
    }
    throw error
  }

  const { error: logError } = await supabase.rpc('record_invite_sent', {
    p_person_id: personId,
  })
  // The code is already gone. Failing the whole call here would tell an admin
  // the send failed and invite a second one, which is worse than an unrecorded
  // send — so this surfaces as a warning to the caller, not an exception.
  if (logError) console.warn('code sent but not recorded:', logError.message)
}

/**
 * 138. The invitation EMAIL — no code in it — for up to 200 people at once,
 * through app.queue_invitations. Each person comes back with an outcome; the
 * server decides who can be invited, never this file.
 *
 * Not `sendSignInCode` in a loop: a code expires in an hour and every one spends
 * GoTrue's /otp rate limit, and an invitation is a welcome that stays valid.
 */
export type InviteOutcome =
  | 'queued'
  | 'already_queued_in_this_wave'
  | 'already_signed_in'
  | 'no_way_to_reach'
  | 'phone_only_no_message'
  | 'address_is_someone_elses_identity'
  | 'not_an_active_member'
  /** 153: outside the caller's region — named, never queued. */
  | 'out_of_scope'

export type InviteResult = { personId: string; displayName: string; outcome: InviteOutcome }

export async function sendInvitations(personIds: string[]): Promise<InviteResult[]> {
  const { data, error } = await supabase.rpc('send_invitations', { p_person_ids: personIds })
  if (error) throw error
  return ((data ?? []) as { person_id: string; display_name: string; outcome: InviteOutcome }[]).map((r) => ({
    personId: r.person_id,
    displayName: r.display_name,
    outcome: r.outcome,
  }))
}

/** One sentence per outcome, for the people who were NOT invited. */
export const INVITE_OUTCOME_LABEL: Record<InviteOutcome, string> = {
  queued: 'invited',
  already_queued_in_this_wave: 'already invited today',
  already_signed_in: 'already signed in',
  no_way_to_reach: 'no work email or mobile on file',
  phone_only_no_message: 'can sign in by text; no email address to send to',
  address_is_someone_elses_identity: "their work email is another person's sign-in",
  not_an_active_member: 'inactive',
  out_of_scope: 'outside your region',
}

/** "12 invited. 3 already signed in. 2 not invited." — the outcome line. */
export function summariseInvites(results: InviteResult[]): string {
  const sent = results.filter((r) => r.outcome === 'queued').length
  const signedIn = results.filter((r) => r.outcome === 'already_signed_in').length
  const today = results.filter((r) => r.outcome === 'already_queued_in_this_wave').length
  const rest = results.length - sent - signedIn - today
  const parts = [`${sent} invited`]
  if (today) parts.push(`${today} already invited today`)
  if (signedIn) parts.push(`${signedIn} already signed in`)
  if (rest) parts.push(`${rest} could not be invited`)
  return parts.join(' · ')
}
